// User lands here after successful payment

import { useEffect, useState } from 'react';
import { supabase } from '../integrations/client.ts';
import { useAuth } from '../context/AuthContext.tsx';
import '../styles/success.css';

export default function SuccessPage({ onNavigateToInquiry, onNavigateToLanding }) {
  const { user, refreshProfile } = useAuth();
  const [checking, setChecking] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');

  useEffect(() => {
    if (user) {
      checkSubscriptionStatus(user);
    } else {
      // If no user yet, wait a moment and try again
      const timeout = setTimeout(() => {
        if (user) {
          checkSubscriptionStatus(user);
        } else {
          setChecking(false);
        }
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [user]);

  const checkSubscriptionStatus = async (currentUser) => {
    try {
      // Poll for subscription status with timeout
      const maxAttempts = 10; // 10 seconds max wait
      const pollInterval = 1000; // Check every 1 second
      let attempts = 0;

      const pollStatus = async () => {
        attempts++;

        // Refresh profile to get latest subscription status
        if (refreshProfile) {
          await refreshProfile();
        }

        // Check user's subscription status
        const { data, error } = await supabase
          .from('profiles')
          .select('subscription_type')
          .eq('id', currentUser?.id)
          .single();

        if (error) {
          console.error('Error fetching subscription status:', error);
          // Don't give up yet, continue polling
          return null;
        }

        return data?.subscription_type || 'free';
      };

      // Poll until we get premium status or timeout
      while (attempts < maxAttempts) {
        const status = await pollStatus();

        if (status === 'premium') {
          // Success! Premium subscription activated
          setSubscriptionStatus('premium');
          setChecking(false);
          return;
        }

        // Wait before next attempt (except on last attempt)
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
      }

      // Timeout reached - use current status
      const finalStatus = currentUser?.subscription_type || 'free';
      setSubscriptionStatus(finalStatus);
    } catch (err) {
      console.error('Error checking subscription:', err);
      // Fallback to user object from auth context
      setSubscriptionStatus(currentUser?.subscription_type || 'free');
    } finally {
      setChecking(false);
    }
  };

  const handleContinue = () => {
    // Navigate to appropriate page based on subscription
    if (subscriptionStatus === 'premium' && onNavigateToInquiry) {
      onNavigateToInquiry();
    } else if (onNavigateToLanding) {
      onNavigateToLanding();
    }
  };

  if (checking) {
    return (
      <div className="success-page-container">
        <div className="processing-card">
          <div className="spinner"></div>
          <h2 className="processing-title">Processing Your Payment</h2>
          <p className="processing-text">Please wait while we confirm your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="success-page-container">
      <div className="success-card">
        {subscriptionStatus === 'premium' ? (
          <>
            {/* Success State */}
            <div className="success-header">
              <div className="success-icon-container">
                <svg
                  className="success-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="success-title">
                Welcome to Premium! 
              </h1>
              <p className="success-subtitle">
                Your subscription has been activated! Thank you for your support!
              </p>
            </div>

            {/* Email Confirmation Note */}
            <p className="confirmation-note">
              A confirmation email has been sent to your inbox
            </p>
          </>
        ) : (
          <>
            {/* Pending/Error State */}
            <div className="pending-header">
              <div className="warning-icon-container">
                <svg
                  className="warning-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h1 className="pending-title">
                Payment Processing
              </h1>
              <p className="pending-subtitle">
                Your payment is being processed. This may take a few moments.
              </p>
              <p className="pending-note">
                If your subscription doesn't activate in 5 minutes, please contact support.
              </p>
            </div>

            <div className="action-buttons">
              <button
                onClick={handleContinue}
                className="primary-button"
                style={{ backgroundColor: '#333', borderRadius: '9px', color: '#E8E5DA' }}
              >
                Get Started
              </button>
              <button
                onClick={() => window.location.reload()}
                className="secondary-button"
              >
                Check Status Again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}